import {
  keepPreviousData,
  useInfiniteQuery,
  useQuery,
} from "@tanstack/react-query";
import { queryClient } from "../../lib/queryClient";
import { queryKeys } from "../../lib/queryKeys";
import {
  getClinicalCareGuidelineDetail,
  getClinicalCareGuidelineRevision,
  getClinicalCareGuidelines,
  lookupClinicalCareGuidelines,
} from "../../services";
import type {
  ClinicalCareGuidelineSort,
  ClinicalCareGuidelinesListParams,
  ClinicalCareGuidelinesListResponse,
} from "../../types/clinical-care-guidelines";
import {
  extractClinicalCareGuidelineDetail,
  extractClinicalCareGuidelineRevision,
  extractClinicalCareGuidelinesList,
  extractClinicalCareGuidelinesLookup,
} from "../../utils/clinical-care-guidelines";

const LIST_STALE_TIME_MS = 30 * 1000;
const DEFAULT_LIST_LIMIT = 30;

export const fetchClinicalCareGuidelinesPage = async (
  params: ClinicalCareGuidelinesListParams
) => {
  const response = await getClinicalCareGuidelines(params);
  return extractClinicalCareGuidelinesList(response?.data);
};

export const fetchClinicalCareGuidelineDetail = async (id: string) => {
  const response = await getClinicalCareGuidelineDetail(id);
  return extractClinicalCareGuidelineDetail(response?.data);
};

export const prefetchClinicalCareGuidelines = () => {
  void queryClient
    .prefetchInfiniteQuery({
      queryKey: queryKeys.clinicalCareGuidelines({
        search: "",
        category: "",
        sort: "conditionAsc",
        limit: DEFAULT_LIST_LIMIT,
      }),
      queryFn: () =>
        fetchClinicalCareGuidelinesPage({
          sort: "conditionAsc",
          limit: DEFAULT_LIST_LIMIT,
        }),
      initialPageParam: undefined as string | undefined,
      getNextPageParam: (lastPage: ClinicalCareGuidelinesListResponse) =>
        lastPage.hasMore && lastPage.nextCursor ? lastPage.nextCursor : undefined,
      staleTime: LIST_STALE_TIME_MS,
    })
    .catch((error) => {
      console.error("Failed to prefetch clinical care guidelines:", error);
    });
};

export const prefetchClinicalCareGuidelineDetail = (id: string) => {
  if (!id) return;
  void queryClient.prefetchQuery({
    queryKey: queryKeys.clinicalCareGuidelineDetail(id),
    queryFn: () => fetchClinicalCareGuidelineDetail(id),
    staleTime: LIST_STALE_TIME_MS,
  });
};

export const useClinicalCareGuidelinesList = (params: {
  search?: string;
  category?: string;
  sort?: ClinicalCareGuidelineSort;
  enabled?: boolean;
}) => {
  const search = params.search?.trim() ?? "";
  const category = params.category?.trim() ?? "";
  const sort = params.sort ?? "conditionAsc";
  const enabled = params.enabled ?? true;

  return useInfiniteQuery({
    queryKey: queryKeys.clinicalCareGuidelines({
      search,
      category,
      sort,
      limit: DEFAULT_LIST_LIMIT,
    }),
    queryFn: ({ pageParam }) =>
      fetchClinicalCareGuidelinesPage({
        search: search || undefined,
        category: category || undefined,
        sort,
        cursor: pageParam as string | undefined,
        limit: DEFAULT_LIST_LIMIT,
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage: ClinicalCareGuidelinesListResponse) =>
      lastPage.hasMore && lastPage.nextCursor ? lastPage.nextCursor : undefined,
    enabled,
    staleTime: LIST_STALE_TIME_MS,
    placeholderData: keepPreviousData,
  });
};

export const useClinicalCareGuidelineDetail = (
  id: string | null,
  options?: { enabled?: boolean }
) => {
  const enabled = options?.enabled ?? Boolean(id);

  return useQuery({
    queryKey: queryKeys.clinicalCareGuidelineDetail(id ?? ""),
    queryFn: () => fetchClinicalCareGuidelineDetail(id!),
    enabled: enabled && Boolean(id),
    staleTime: LIST_STALE_TIME_MS,
  });
};

export const useClinicalCareGuidelineRevision = (
  id: string | null,
  revisionNumber: number | string | null,
  options?: { enabled?: boolean }
) => {
  const enabled =
    (options?.enabled ?? true) && Boolean(id) && revisionNumber != null;

  return useQuery({
    queryKey: queryKeys.clinicalCareGuidelineRevision(
      id ?? "",
      revisionNumber ?? ""
    ),
    queryFn: async () => {
      const response = await getClinicalCareGuidelineRevision(
        id!,
        revisionNumber!
      );
      return extractClinicalCareGuidelineRevision(response?.data);
    },
    enabled,
    staleTime: LIST_STALE_TIME_MS,
  });
};

export const useClinicalCareGuidelinesLookup = (
  conditions: string[],
  options?: { enabled?: boolean; includeContent?: boolean }
) => {
  const includeContent = options?.includeContent ?? true;
  const enabled = (options?.enabled ?? true) && conditions.length > 0;

  return useQuery({
    queryKey: queryKeys.clinicalCareGuidelinesLookup(conditions),
    queryFn: async () => {
      const response = await lookupClinicalCareGuidelines({
        conditions,
        includeContent,
      });
      return extractClinicalCareGuidelinesLookup(response?.data);
    },
    enabled,
    staleTime: LIST_STALE_TIME_MS,
  });
};
